/**
 * Disclaimer — The Chi Alliance
 * Visual direction: companion legal page to Privacy Policy, using the same deep-obisidian editorial field,
 * 72px DM Serif Display hero scale, and restrained magenta-to-gold accents for calm legal clarity.
 */
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { FileWarning, Mail } from "lucide-react";

const disclaimerStatements = [
  "The views, opinions, and recommendations expressed by speakers, sponsors, members, partners, and contributors are their own and do not necessarily reflect the views of The CHI Alliance, its leadership, affiliates, sponsors, or members.",
  "Participation in The CHI Alliance community, events, programs, discussions, and educational offerings does not constitute legal, financial, business, human resources, medical, tax, or professional advice. Participants should seek appropriate professional counsel for specific situations and decisions.",
  "While we strive to provide high-quality information and experiences, The CHI Alliance makes no representations or warranties regarding the completeness, accuracy, reliability, or suitability of any information shared through our community or events.",
  "By participating in The CHI Alliance community, events, and programs, you acknowledge and agree that participation is voluntary and at your own discretion.",
  "We are committed to fostering a respectful, inclusive, collaborative, and professional community where individuals can connect, learn, collaborate, and grow together.",
];

export default function Disclaimer() {
  return (
    <div className="disclaimer-page">
      <Navigation />

      <main>
        <section className="disclaimer__hero" aria-labelledby="disclaimer-title">
          <div className="disclaimer__orb disclaimer__orb--magenta" aria-hidden="true" />
          <div className="disclaimer__orb disclaimer__orb--gold" aria-hidden="true" />
          <div className="container disclaimer__hero-inner">
            <div className="disclaimer__eyebrow">
              <FileWarning size={15} strokeWidth={1.8} aria-hidden="true" />
              <span>Information &amp; participation</span>
            </div>
            <h1 id="disclaimer-title" className="disclaimer__title">
              Our <span>Disclaimer</span>
            </h1>
            <p className="disclaimer__intro">
              The information, content, events, educational sessions, presentations, discussions, and materials provided by The CHI Alliance are intended for informational, educational, networking, and community-building purposes only.
            </p>
          </div>
        </section>

        <section className="disclaimer__content-section">
          <div className="container disclaimer__layout">
            <aside className="disclaimer__aside" aria-label="Disclaimer context">
              <div className="disclaimer__aside-rule" aria-hidden="true" />
              <p className="disclaimer__aside-label">Please note</p>
              <p className="disclaimer__aside-copy">
                Participation is voluntary. Seek qualified professional guidance for decisions specific to your situation.
              </p>
              <a className="disclaimer__contact-link" href="mailto:info@thechialliance.com">
                <Mail size={15} strokeWidth={1.8} aria-hidden="true" />
                <span>Contact our team</span>
              </a>
            </aside>

            <article className="disclaimer__document">
              <div className="disclaimer__statements">
                {disclaimerStatements.map((statement, index) => (
                  <div className="disclaimer__statement" key={statement}>
                    <span className="disclaimer__statement-index" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p>{statement}</p>
                  </div>
                ))}
              </div>

              <section className="disclaimer__section" aria-labelledby="disclaimer-consent">
                <h2 id="disclaimer-consent">Consent</h2>
                <p>By using our website, you hereby consent to our disclaimer and agree to its terms.</p>
              </section>

              <section className="disclaimer__section disclaimer__section--update" aria-labelledby="disclaimer-update">
                <h2 id="disclaimer-update">Update</h2>
                <p>Should we update, amend, or make any changes to this document, those changes will be prominently posted here.</p>
              </section>
            </article>
          </div>
        </section>
      </main>

      <Footer />

      <style>{`
        .disclaimer-page {
          min-height: 100vh;
          overflow: hidden;
          color: #F8FAFC;
          background: #080C18;
          font-family: 'Inter', sans-serif;
        }

        .disclaimer__hero {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          background:
            radial-gradient(circle at 80% -2%, rgba(220,61,145,0.18), transparent 28rem),
            radial-gradient(circle at 88% 106%, rgba(240,168,58,0.14), transparent 26rem),
            linear-gradient(115deg, #080C18 12%, #0C1020 54%, #15101B 100%);
        }

        .disclaimer__hero::after {
          position: absolute;
          z-index: -1;
          inset: 0;
          content: '';
          opacity: .42;
          background-image:
            linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px);
          background-size: 52px 52px;
          mask-image: linear-gradient(90deg, #000, transparent 88%);
        }

        .disclaimer__orb {
          position: absolute;
          z-index: -1;
          width: 22rem;
          height: 22rem;
          border-radius: 999px;
          filter: blur(76px);
          opacity: .29;
          pointer-events: none;
        }

        .disclaimer__orb--magenta { top: -13rem; right: 19%; background: #DC3D91; }
        .disclaimer__orb--gold { right: -6rem; bottom: -15rem; background: #F0A83A; }

        .disclaimer__hero-inner {
          min-height: clamp(23rem, 43vw, 35rem);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding-top: clamp(8rem, 15vw, 12.5rem);
          padding-bottom: clamp(3.75rem, 8vw, 7rem);
        }

        .disclaimer__eyebrow {
          display: inline-flex;
          width: fit-content;
          align-items: center;
          gap: .5rem;
          margin-bottom: 1.4rem;
          padding: .48rem .75rem;
          border: 1px solid rgba(220,61,145,.31);
          border-radius: 999px;
          color: #F8B5DA;
          background: rgba(220,61,145,.09);
          font-family: 'Space Grotesk', sans-serif;
          font-size: .69rem;
          font-weight: 600;
          letter-spacing: .08em;
          text-transform: uppercase;
        }

        .disclaimer__title {
          max-width: 820px;
          margin: 0;
          color: #FFFFFF;
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: 72px;
          font-weight: 400;
          letter-spacing: -.052em;
          line-height: .93;
        }

        .disclaimer__title span {
          display: inline-block;
          color: transparent;
          background: linear-gradient(100deg, #DC3D91 4%, #E36299 36%, #F0A83A 96%);
          background-clip: text;
          -webkit-background-clip: text;
        }

        .disclaimer__intro {
          max-width: 760px;
          margin: 1.55rem 0 0;
          color: #CBD5E1;
          font-size: clamp(1rem, 1.35vw, 1.14rem);
          line-height: 1.75;
        }

        .disclaimer__content-section {
          position: relative;
          padding: clamp(4rem, 8vw, 7.5rem) 0 clamp(5rem, 9vw, 8.5rem);
          background:
            linear-gradient(115deg, rgba(255,255,255,.018) 1px, transparent 1px),
            linear-gradient(25deg, #080C18 0%, #0A0E1A 61%, #0B101B 100%);
          background-size: 42px 42px, auto;
        }

        .disclaimer__layout {
          display: grid;
          grid-template-columns: minmax(0,.38fr) minmax(0,1fr);
          gap: clamp(3rem, 8vw, 10rem);
          align-items: start;
        }

        .disclaimer__aside {
          position: sticky;
          top: 7.5rem;
          max-width: 17rem;
          padding-top: .15rem;
        }

        .disclaimer__aside-rule {
          width: 4rem;
          height: 2px;
          margin-bottom: 1.2rem;
          background: linear-gradient(90deg, #DC3D91, #F0A83A);
        }

        .disclaimer__aside-label {
          margin: 0 0 .85rem;
          color: #F8FAFC;
          font-family: 'Space Grotesk', sans-serif;
          font-size: .78rem;
          font-weight: 700;
          letter-spacing: .1em;
          text-transform: uppercase;
        }

        .disclaimer__aside-copy {
          margin: 0;
          color: #94A3B8;
          font-size: .92rem;
          line-height: 1.75;
        }

        .disclaimer__contact-link {
          display: inline-flex;
          align-items: center;
          gap: .55rem;
          margin-top: 1.6rem;
          color: #F8B5DA;
          font-family: 'Space Grotesk', sans-serif;
          font-size: .81rem;
          font-weight: 600;
          text-decoration: none;
          transition: color 180ms cubic-bezier(.23,1,.32,1), transform 180ms cubic-bezier(.23,1,.32,1);
        }

        .disclaimer__contact-link:hover { color: #FFFFFF; transform: translateX(3px); }
        .disclaimer__contact-link:focus-visible { outline: 2px solid #F0A83A; outline-offset: 5px; border-radius: 3px; }

        .disclaimer__document {
          max-width: 46rem;
          padding: clamp(1.55rem, 4vw, 3.2rem);
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 1.4rem;
          background: linear-gradient(145deg, rgba(17,23,39,.92), rgba(10,14,26,.94));
          box-shadow: 0 30px 80px rgba(0,0,0,.23);
        }

        .disclaimer__statements { display: grid; gap: 0; }

        .disclaimer__statement {
          display: grid;
          grid-template-columns: 2.4rem minmax(0,1fr);
          gap: 1.1rem;
          padding: 1.55rem 0;
          border-bottom: 1px solid rgba(255,255,255,.09);
        }

        .disclaimer__statement:first-child { padding-top: 0; }

        .disclaimer__statement-index {
          padding-top: .16rem;
          color: #F0A83A;
          font-family: 'Space Grotesk', sans-serif;
          font-size: .68rem;
          font-weight: 700;
          letter-spacing: .08em;
        }

        .disclaimer__statement p,
        .disclaimer__section p {
          margin: 0;
          color: #B8C4D3;
          font-size: .98rem;
          line-height: 1.8;
        }

        .disclaimer__section { margin-top: 2.8rem; }

        .disclaimer__section h2 {
          margin: 0 0 .9rem;
          color: #FFFFFF;
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: clamp(1.72rem, 2.5vw, 2.24rem);
          font-weight: 400;
          letter-spacing: -.026em;
          line-height: 1.08;
        }

        .disclaimer__section--update {
          padding: 1.65rem;
          border: 1px solid rgba(240,168,58,.2);
          border-radius: 1rem;
          background: linear-gradient(135deg, rgba(240,168,58,.08), rgba(220,61,145,.07));
        }

        @media (max-width: 820px) {
          .disclaimer__layout { grid-template-columns: 1fr; gap: 2.8rem; }
          .disclaimer__aside { position: static; max-width: 31rem; }
          .disclaimer__document { max-width: none; }
        }

        @media (max-width: 560px) {
          .disclaimer__hero-inner { min-height: 24rem; padding-top: 7.2rem; }
          .disclaimer__title { font-size: clamp(3rem, 15vw, 4.5rem); }
          .disclaimer__document { padding: 1.35rem; border-radius: 1rem; }
          .disclaimer__statement { grid-template-columns: 1.9rem minmax(0,1fr); gap: .72rem; }
          .disclaimer__section--update { padding: 1.3rem; }
        }

        @media (prefers-reduced-motion: reduce) {
          .disclaimer__contact-link { transition: none; }
        }
      `}</style>
    </div>
  );
}
